import { useState } from "react";
import { Link } from "react-router-dom";
import React from "react";
import PropTypes from "prop-types";
import { v4 as uuidv4 } from "uuid";
import { Table as MuiTable } from "@mui/material";
import TableBody from "@mui/material/TableBody";
import TableContainer from "@mui/material/TableContainer";
import TableRow from "@mui/material/TableRow";
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import colors from "assets/theme/base/colors";
import typography from "assets/theme/base/typography";
import Location from "layouts/tables/Location";
import Comment from "layouts/tables/Comment";
import {
  DndContext,
  DragOverlay,
  closestCenter,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  DragHandle,
  FullRowOverlay,
  dropAnimation,
  sortAutoScroll,
  useVerticalSortSensors,
  verticalSortModifiers,
} from "./dragDrop";

function DisciplineRowCells({ row, rowIndex, columns, handleProps }) {
  const disciplineId = row?.disciplineId;
  const isEvenRow = rowIndex % 2 === 0;

  return columns.map(({ name, align }) => {
    if (name === " ") {
      return (
        <SoftBox
          key={`drag-${disciplineId}`}
          component="td"
          px={1}
          py={1.5}
          textAlign="center"
          sx={{
            backgroundColor: isEvenRow ? "white" : "#F1F4F8",
            verticalAlign: "middle",
          }}
        >
          <DragHandle {...handleProps} />
        </SoftBox>
      );
    }

    return (
      <SoftBox
        key={`${disciplineId}-${name}`}
        component="td"
        px={1}
        py={1.5}
        textAlign={align}
        sx={{
          backgroundColor: isEvenRow ? "white" : "#F1F4F8",
          verticalAlign: "middle",
        }}
      >
        <SoftTypography
          variant="button"
          fontWeight="regular"
          color="secondary"
          sx={{ display: "inline-block", width: "max-content" }}
        >
          {row[name]}
        </SoftTypography>
      </SoftBox>
    );
  });
}

function SortableDisciplineRow({
  row,
  rowIndex,
  columns,
  onRowClick,
}) {
  const disciplineId = row?.disciplineId;
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: String(disciplineId) });
  const isEvenRow = rowIndex % 2 === 0;
  const linkPath = row.linkPath;
  const noLinkCells = row.noLinkCells || [];
  const tableRow = (
    <DisciplineRowCells
      row={row}
      rowIndex={rowIndex}
      columns={columns}
      handleProps={{ attributes, listeners }}
    />
  );

  return (
    <TableRow
      ref={setNodeRef}
      onClick={() => onRowClick?.(disciplineId)}
      sx={{
        transform: isDragging ? "none" : CSS.Translate.toString(transform),
        transition: isDragging ? "none" : transition,
        opacity: isDragging ? 0 : 1,
        cursor: disciplineId ? "pointer" : "default",
      }}
    >
      {linkPath ? (
        <>
          {tableRow.map((cell, index) => (
            <React.Fragment key={index}>
              {noLinkCells.includes(columns[index].name) ? (
                cell
              ) : (
                <Link
                  to={linkPath}
                  style={{
                    textDecoration: "none",
                    color: "inherit",
                    display: "contents",
                  }}
                >
                  {cell}
                </Link>
              )}
            </React.Fragment>
          ))}
        </>
      ) : (
        tableRow
      )}
    </TableRow>
  );
}

function Table({
  columns,
  rows,
  expandDisciplineId,
  onToggleExpand,
  onDisciplineReorder,
  onInspectionTypeReorder,
  onCommentReorder,
}) {
  const { lightBlack, shadow } = colors;
  const { size, fontWeightBold } = typography;
  const sensors = useVerticalSortSensors();
  const [expandedRow, setExpandedRow] = useState("");
  const [activeId, setActiveId] = useState(null);
  const [overlaySize, setOverlaySize] = useState({ width: 0, height: 0 });

  React.useEffect(() => {
    setExpandedRow(expandDisciplineId || "");
  }, [expandDisciplineId]);

  const sortableIds = rows
    .map((row) => row?.disciplineId)
    .filter(Boolean)
    .map(String);

  const activeRow = rows.find((row) => String(row.disciplineId) === String(activeId));

  const renderColumns = columns.map(({ name, align, width }) => (
    <SoftBox
      key={name}
      component="th"
      width={width || "auto"}
      pt={1.75}
      pb={1.75}
      pl={align === "left" ? 3 : 1}
      pr={align === "right" ? 3 : 1}
      textAlign={align}
      fontSize={size.lg}
      fontWeight={fontWeightBold}
      color={lightBlack.main}
      backgroundColor={shadow.main}
      borderBottom="none !important"
    >
      {name}
    </SoftBox>
  ));

  const toggleExpand = (disciplineId) => {
    if (!disciplineId) return;
    if (onToggleExpand) {
      onToggleExpand((prev) => (prev === disciplineId ? "" : disciplineId));
    } else {
      setExpandedRow((prev) => (prev === disciplineId ? "" : disciplineId));
    }
  };

  const handleDragStart = ({ active }) => {
    setActiveId(String(active.id));
    const rect = active.rect.current.initial;
    setOverlaySize({
      width: rect?.width || 0,
      height: rect?.height || 0,
    });
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;
    setActiveId(null);
    if (!over || active.id === over.id || !onDisciplineReorder) {
      return;
    }
    const oldIndex = sortableIds.indexOf(String(active.id));
    const newIndex = sortableIds.indexOf(String(over.id));
    if (oldIndex < 0 || newIndex < 0) {
      return;
    }
    onDisciplineReorder(arrayMove(sortableIds, oldIndex, newIndex));
  };

  const renderRows = rows.map((row, key) => {
    const rowKey = `row-${key}`;
    const isEvenRow = key % 2 === 0;
    const linkPath = row.linkPath;
    const noLinkCells = row.noLinkCells || [];
    const inspectionTypes = row?.inspectionTypes;
    const comments = row?.comments;
    const disciplineId = row?.disciplineId;
    const isExpanded = expandedRow === disciplineId;

    const tableRow = columns.map(({ name, align }) => (
      <SoftBox
        key={uuidv4()}
        component="td"
        px={1}
        py={1.5}
        textAlign={align}
        sx={{
          backgroundColor: isEvenRow ? "white" : "#F1F4F8",
          verticalAlign: "middle",
        }}
      >
        <SoftTypography
          variant="button"
          fontWeight="regular"
          color="secondary"
          sx={{ display: "inline-block", width: "max-content" }}
        >
          {row[name]}
        </SoftTypography>
      </SoftBox>
    ));

    return (
      <React.Fragment key={rowKey}>
        <TableRow
          onClick={() => toggleExpand(disciplineId)}
          sx={{ cursor: disciplineId ? "pointer" : "default" }}
        >
          {linkPath ? (
            <>
              {tableRow.map((cell, index) => (
                <React.Fragment key={index}>
                  {noLinkCells.includes(columns[index].name) ? (
                    cell
                  ) : (
                    <Link
                      to={linkPath}
                      style={{
                        textDecoration: "none",
                        color: "inherit",
                        display: "contents",
                      }}
                    >
                      {cell}
                    </Link>
                  )}
                </React.Fragment>
              ))}
            </>
          ) : (
            tableRow
          )}
        </TableRow>

        {isExpanded && (
          <TableRow>
            <td colSpan={columns.length}>
              <SoftBox
                sx={{
                  backgroundColor: "white",
                  borderRadius: "4px",
                  marginTop: "5px",
                }}
              >
                <Location inspectionTypes={inspectionTypes} disciplineId={disciplineId} />
                <Comment comments={comments} disciplineId={disciplineId} />
              </SoftBox>
            </td>
          </TableRow>
        )}
      </React.Fragment>
    );
  });

  const sortableEnabled = typeof onDisciplineReorder === "function" && sortableIds.length > 0;

  return (
    <TableContainer
      sx={{
        borderRadius: 0,
        height: "calc(100vh - 200px)",
        overflowX: "hidden",
        overflowY: "auto",
        overscrollBehavior: "contain",
      }}
    >
      <MuiTable
        sx={{
          borderRadius: 0,
          overflow: "hidden",
        }}
      >
        <SoftBox
          component="thead"
          sx={{ backgroundColor: "white", position: "sticky", top: 0, zIndex: 10 }}
        >
          <TableRow>{renderColumns}</TableRow>
        </SoftBox>
        {sortableEnabled ? (
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            modifiers={verticalSortModifiers}
            autoScroll={sortAutoScroll}
            onDragStart={handleDragStart}
            onDragCancel={() => setActiveId(null)}
            onDragEnd={handleDragEnd}
          >
            <SortableContext items={sortableIds} strategy={verticalListSortingStrategy}>
              <TableBody>
                {rows.map((row, key) => {
                  const disciplineId = row?.disciplineId;
                  const isExpanded =
                    expandedRow === disciplineId && String(activeId) !== String(disciplineId);

                  return (
                    <React.Fragment key={String(disciplineId || key)}>
                      <SortableDisciplineRow
                        row={row}
                        rowIndex={key}
                        columns={columns}
                        onRowClick={toggleExpand}
                      />
                      {isExpanded && (
                        <TableRow>
                          <td colSpan={columns.length}>
                            <SoftBox
                              sx={{
                                backgroundColor: "white",
                                borderRadius: "4px",
                                marginTop: "5px",
                                overflow: "hidden",
                              }}
                            >
                              <Location
                                inspectionTypes={row?.inspectionTypes}
                                disciplineId={disciplineId}
                                onReorder={onInspectionTypeReorder}
                              />
                              <Comment
                                comments={row?.comments}
                                disciplineId={disciplineId}
                                onReorder={onCommentReorder}
                              />
                            </SoftBox>
                          </td>
                        </TableRow>
                      )}
                    </React.Fragment>
                  );
                })}
              </TableBody>
            </SortableContext>
            <DragOverlay dropAnimation={dropAnimation} adjustScale={false} zIndex={20}>
              {activeRow ? (
                <FullRowOverlay
                  width={overlaySize.width}
                  height={overlaySize.height}
                  backgroundColor={
                    rows.indexOf(activeRow) % 2 === 0 ? "white" : "#F1F4F8"
                  }
                >
                  <DisciplineRowCells
                    row={activeRow}
                    rowIndex={rows.indexOf(activeRow)}
                    columns={columns}
                    handleProps={{}}
                  />
                </FullRowOverlay>
              ) : null}
            </DragOverlay>
          </DndContext>
        ) : (
          <TableBody>{renderRows}</TableBody>
        )}
      </MuiTable>
    </TableContainer>
  );
}

Table.defaultProps = {
  columns: [],
  rows: [{}],
};

Table.propTypes = {
  columns: PropTypes.arrayOf(PropTypes.object),
  rows: PropTypes.arrayOf(PropTypes.object),
};

export default Table;
